<template>
  <BfFlex>
    <BfInput
      v-model="filters.FreeTextSearch"
      :name="t('common.search.filterBrandOrModel')"
      :placeholder="t('common.search.filterBrandOrModel')"
      style="flex: 1"
      @keyup="updateSearch"
    />
  </BfFlex>

  <div class="handlebar-selection__results">
    <BfLoader v-if="loading" />
    <BfList v-if="handlebarDataFiltered.length > 0">
      <BfListItem
        v-for="(handlebar, i) in handlebarDataFiltered"
        :key="i"
        :text="`${handlebar.HANDLEBAR_BRAND} ${handlebar.HANDLEBAR_MODEL} (${handlebar.HANDLEBAR_WIDTH} ${t('common.unit.mm')})`"
        :state="
          handlebar === selected ? ListItemState.Active : ListItemState.Enabled
        "
        @click="select(handlebar)"
      />
    </BfList>
    <div v-else>
      <p v-if="!loading">{{ t('common.search.noResults') }}</p>
    </div>
  </div>

  <SelectionDetails v-if="selected">
    <SelectionDetailsItem
      :label="t('setup.handlebar.width')"
      :value="`${selected.HANDLEBAR_WIDTH} ${t('common.unit.mm')}`"
      :icon="widthIcon()"
    />
    <SelectionDetailsItem
      v-if="bikeHoodsType === BikeHoodsType.DROPBAR"
      :label="t('setup.handlebar.reach')"
      :value="`${selected.HANDLEBAR_REACH} ${t('common.unit.mm')}`"
      icon="/icons/filters/handlebar__road--reach.svg"
    />
    <SelectionDetailsItem
      v-if="bikeHoodsType === BikeHoodsType.DROPBAR"
      :label="t('setup.handlebar.drop')"
      :value="`${selected.HANDLEBAR_DROP} ${t('common.unit.mm')}`"
      icon="/icons/filters/handlebar__road--drop.svg"
    />
    <SelectionDetailsItem
      v-if="
        bikeHoodsType === BikeHoodsType.DROPBAR ||
        bikeHoodsType === BikeHoodsType.FLATBAR
      "
      :label="t('setup.handlebar.sweep')"
      :value="`${selected.BACK_SWEEP_ANGLE} ${t('common.unit.degreesSign')}`"
      :icon="sweepIcon()"
    />
    <SelectionDetailsItem
      v-if="bikeHoodsType === BikeHoodsType.DROPBAR"
      :label="t('setup.handlebar.flare')"
      :value="`${selected.FLARE_ANGLE} ${t('common.unit.degreesSign')}`"
      icon="/icons/filters/handlebar__road--flare.svg"
    />
  </SelectionDetails>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/store/userStore'
import { useHandlebarStore } from '@/store/handlebarStore'
import { useBikeStore } from '@/store/bikeStore'
import {
  ListItemState,
  BfLoader,
  BfList,
  BfListItem,
  BfFlex,
  BfInput,
} from '@finetic/bikefitting-ui'
import { useDebounceFn } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import SelectionDetails from '@/components/setup/SelectionDetails.vue'
import SelectionDetailsItem from '@/components/setup/SelectionDetailsItem.vue'
import { BikeHoodsType } from '@/types/bike'
import handlebarService from '@/service/handlebarService'

const { t } = useI18n()
const userStore = useUserStore()
const handlebarStore = useHandlebarStore()
const bikeStore = useBikeStore()
const { sessionId } = storeToRefs(userStore)
const { handlebarData, filters } = storeToRefs(handlebarStore)
const { bikeHoodsType } = storeToRefs(bikeStore)
const loading = ref(false)
const selected = ref()

const handlebarFilter = computed(() => {
  switch (bikeHoodsType.value) {
    case BikeHoodsType.DROPBAR:
      return 1
    case BikeHoodsType.FLATBAR:
      return 200
    case BikeHoodsType.TRIBAR:
      return 300
    default:
      return 1
  }
})

const handlebarDataFiltered = computed(() => {
  return handlebarData.value.filter(
    (obj) => obj.HANDLEBAR_FILTER === handlebarFilter.value,
  )
})

const widthIcon = () => {
  return handlebarService.getWidthIcon(bikeHoodsType.value)
}

const sweepIcon = () => {
  return handlebarService.getSweepIcon(bikeHoodsType.value)
}

const emit = defineEmits(['select'])

handlebarStore.reset()

const select = (handlebar: any) => {
  selected.value = handlebar
  emit('select', handlebar)
}

const search = () => {
  loading.value = true

  handlebarStore.reset()
  handlebarStore.retrieveHandlebars(sessionId?.value ?? '').then(() => {
    loading.value = false
  })
}

const updateSearch = useDebounceFn(() => {
  search()
}, 300)

search()
</script>

<style lang="scss">
.handlebar-selection {
  display: flex;
  height: 100%;

  &__results {
    margin-top: 1.2rem;
    border: solid 1px var(--color-dark-10);
    border-radius: 0.4rem;
    height: 32rem;
    overflow-y: auto;

    p {
      text-align: center;
      padding: 1.6rem;
      color: var(--color-dark-40);
    }
  }
}
</style>
